import { useEffect, useRef } from 'react';
import t from 'react-translate';
import { useFormContext } from 'react-hook-form';
import { config } from '@config/pendo.config.json';

import {
  IntegrationAuth,
  MentorshipProgramSession,
} from 'redux/schemas/models/mentoring-program-sessions';

import useCalendarIntegration from 'athena/hooks/use-calendar-integration';
import AthenaTextInput from 'athena/components/text-input';
import VirtualMeeting from './virtual-meeting/virtual-meeting';

const shouldShowVirtualMeeting = (
  joinUrl: MentorshipProgramSession['joinUrl'],
  integrations: IntegrationAuth,
  calendarIntegration: ReturnType<typeof useCalendarIntegration>,
) => {
  if (joinUrl) return true;
  return integrations?.source?.length > 0 || (
    calendarIntegration.hasMs365CalendarIntegration
    && calendarIntegration.isCalendarIntegrationMissing
  );
};

type SessionScheduleLocationProps = Pick<MentorshipProgramSession, 'id'> & {
  integrations: IntegrationAuth;
  isEdit: boolean;
  openIntegrationsModal: () => void;
};

const SessionScheduleLocation = (props: SessionScheduleLocationProps) => {
  const {
    id,
    integrations,
    isEdit,
    openIntegrationsModal,
  } = props;

  const inputRef = useRef(null);
  const { watch } = useFormContext();
  const joinUrl = watch('joinUrl');

  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE;
  const dataQATags = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout;

  const calendarIntegration = useCalendarIntegration();

  useEffect(() => {
    if (inputRef.current && !isEdit) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  return (
    <div className='location-info'>
      <div className='session-time mb-1'>{translations.TITLE()}</div>
      <div className='title'>
        <AthenaTextInput
          withForm
          name='title'
          maxLength={255}
          dataQa={dataQATags.sessionTitleInputField}
          ref={inputRef}
          required
        />
      </div>
      {shouldShowVirtualMeeting(
        joinUrl,
        integrations,
        calendarIntegration,
      ) && (
        <VirtualMeeting
          integrations={integrations}
          openIntegrationsModal={openIntegrationsModal}
        />
      )}
      <div className='session-time mb-1'>{translations.LOCATION()}</div>
      <div className='location'>
        <AthenaTextInput
          withForm
          name='location'
          maxLength={190}
          dataQa={dataQATags.locationInputField}
          dataQaId={`${dataQATags.locationInputField}_${id ?? '0'}`}
        />
      </div>
    </div>
  );
};

export default SessionScheduleLocation;
